/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { defaultListingPageSize } from "Constants/BenefitConstants";

import {
  BenefitActionTypes,
  IBenefitsFilterValueAdd,
  IBenefitsFilterValueRemove,
  IBenefitsFilterValueUpdate,
  ISearchBenefits,
  ISearchBenefitsFail,
  ISearchBenefitsSuccess,
  IUpdateCurrentPage,
  IUpdatePageSize,
} from "./BenefitAction";
import {
  BENEFITS_FILTER_VALUE_ADD,
  BENEFITS_FILTER_VALUE_REMOVE,
  BENEFITS_FILTER_VALUE_UPDATE,
  BENEFITS_RESET,
  BENEFITS_SEARCH,
  BENEFITS_SEARCH_FAIL,
  BENEFITS_SEARCH_SUCCESS,
  UPDATE_BENEFIT_LISTING_CURRENT_PAGE,
  UPDATE_BENEFIT_LISTING_PAGE_SIZE,
} from "./BenefitActionTypes";
import { IBenefitState } from "./IBenefitState";

const INITIAL_STATE: IBenefitState = {
  isLoadingBenefits: false,
  benefits: [],
  benefitsFilterValues: [],
  loadBenefitsError: "",
  currentPage: 1,
  pageSize: defaultListingPageSize,
  searchText: "",
  filteredCount: 0,
  unclaimedCount: 0,
};

const applySearchBenefits = (state: IBenefitState, action: ISearchBenefits): IBenefitState => ({
  ...state,
  benefits: [],
  isLoadingBenefits: true,
  searchText: action.searchText,
  currentPage: action.searchPage,
  loadBenefitsError: "",
  currentTenant: action.tenant,
  currentBenefitName: action.benefitName,
});

const applySearchBenefitsSuccess = (
  state: IBenefitState,
  action: ISearchBenefitsSuccess
): IBenefitState => ({
  ...state,
  benefits: action.benefits,
  currentPage: action.searchPage,
  isLoadingBenefits: false,
  filteredCount: action.filteredCount,
  unclaimedCount: action.unclaimedCount,
  loadBenefitsError: "",
});

const applySearchBenefitsFail = (
  state: IBenefitState,
  action: ISearchBenefitsFail
): IBenefitState => ({
  ...state,
  loadBenefitsError: action.error,
  isLoadingBenefits: false,
});

const applyResetBenefits = (): IBenefitState => INITIAL_STATE;

const applyUpdateCurrentPage = (
  state: IBenefitState,
  action: IUpdateCurrentPage
): IBenefitState => ({
  ...state,
  currentPage: action.currentPage,
});

const applyUpdatePageSize = (state: IBenefitState, action: IUpdatePageSize): IBenefitState => ({
  ...state,
  pageSize: action.pageSize,
});

const addBenefitsFilterValue = (
  state: IBenefitState,
  action: IBenefitsFilterValueAdd
): IBenefitState => ({
  ...state,
  benefitsFilterValues: state.benefitsFilterValues.concat([action.filterValue]),
});

const removeBenefitsFilterValue = (
  state: IBenefitState,
  action: IBenefitsFilterValueRemove
): IBenefitState => ({
  ...state,
  benefitsFilterValues: state.benefitsFilterValues.filter(
    (item) =>
      !(item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value)
  ),
});

const updateBenefitsFilterValue = (
  state: IBenefitState,
  action: IBenefitsFilterValueUpdate
): IBenefitState => ({
  ...state,
  benefitsFilterValues: state.benefitsFilterValues
    .filter((item) => !(item.fieldName === action.filterValue.fieldName))
    .concat([action.filterValue]),
});

function benefitReducer(state = INITIAL_STATE, action: BenefitActionTypes): IBenefitState {
  switch (action.type) {
    case BENEFITS_SEARCH: {
      return applySearchBenefits(state, action);
    }
    case BENEFITS_SEARCH_FAIL: {
      return applySearchBenefitsFail(state, action);
    }
    case BENEFITS_SEARCH_SUCCESS: {
      return applySearchBenefitsSuccess(state, action);
    }
    case BENEFITS_RESET: {
      return applyResetBenefits();
    }
    case UPDATE_BENEFIT_LISTING_CURRENT_PAGE: {
      return applyUpdateCurrentPage(state, action);
    }
    case UPDATE_BENEFIT_LISTING_PAGE_SIZE: {
      return applyUpdatePageSize(state, action);
    }
    case BENEFITS_FILTER_VALUE_ADD: {
      return addBenefitsFilterValue(state, action);
    }
    case BENEFITS_FILTER_VALUE_REMOVE: {
      return removeBenefitsFilterValue(state, action);
    }
    case BENEFITS_FILTER_VALUE_UPDATE: {
      return updateBenefitsFilterValue(state, action);
    }
    default:
      return state;
  }
}

export default benefitReducer;
